import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sun, Moon } from "lucide-react";
import { useTheme } from "./ThemeProvider";
import { useEffect, useState } from "react";

const MagneticLink = ({ to, children, testid, hash }) => {
  const location = useLocation();
  const handleClick = (e) => {
    if (hash && location.pathname === "/") {
      e.preventDefault();
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  };
  return (
    <Link
      to={to}
      onClick={handleClick}
      data-cursor="hover"
      data-testid={testid}
      className="magnetic link-underline text-sm tracking-wide text-[color:var(--nn-text)]/80 hover:text-[color:var(--nn-text)] transition-colors"
    >
      {children}
    </Link>
  );
};

const Navigation = () => {
  const { theme, toggle } = useTheme();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "py-3 bg-[color:var(--nn-bg)]/85 backdrop-blur-xl border-b border-[color:var(--nn-border)]"
          : "py-5 md:py-7"
      }`}
      data-testid="site-nav"
    >
      <div className="mx-auto max-w-[1600px] px-5 md:px-10 flex items-center justify-between">
          <Link
            to="/"
            data-cursor="hover"
            data-testid="nav-logo"
            className={`flex items-center gap-2 transition-all ${scrolled ? "scale-95" : ""}`}
          >
            <div className="w-8 h-8 rounded-full border border-[color:var(--nn-border)] grid place-items-center relative overflow-hidden bg-[color:var(--nn-accent)] text-[color:var(--nn-accent-ink)]">
              <span className="font-display text-base font-semibold">n</span>
            </div>
            <div className="hidden md:flex flex-col leading-none">
              <span className="font-display text-base font-medium tracking-tight">Nirav Nayak</span>
            </div>
          </Link>

        <nav className="hidden md:flex items-center gap-8" aria-label="Primary">
          <MagneticLink to="/#work" hash="#work" testid="nav-work">Work</MagneticLink>
          <MagneticLink to="/#about" hash="#about" testid="nav-about">About</MagneticLink>
          <MagneticLink to="/#process" hash="#process" testid="nav-process">Process</MagneticLink>
          <MagneticLink to="/#playground" hash="#playground" testid="nav-playground">Playground</MagneticLink>
          <MagneticLink to="/#contact" hash="#contact" testid="nav-contact">Contact</MagneticLink>
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={toggle}
            aria-label="Toggle theme"
            data-testid="theme-toggle"
            data-cursor="hover"
            className="relative w-11 h-11 grid place-items-center rounded-full border border-[color:var(--nn-border)] bg-[color:var(--nn-surface)]/60 backdrop-blur hover:border-[color:var(--nn-accent)]/50 transition-colors"
          >
            <AnimatePresence mode="wait">
              {theme === "dark" ? (
                <motion.div
                  key="moon"
                  initial={{ rotate: -60, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 60, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Moon size={16} />
                </motion.div>
              ) : (
                <motion.div
                  key="sun"
                  initial={{ rotate: -60, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 60, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Sun size={16} />
                </motion.div>
              )}
            </AnimatePresence>
          </button>
          <a
            href="/#contact"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
            }}
            data-cursor="hover"
            data-cursor-label="Let's Talk"
            data-testid="nav-cta"
            className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[color:var(--nn-accent)] text-[color:var(--nn-accent-ink)] text-sm font-medium hover:gap-3 transition-all"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--nn-accent-ink)] animate-pulse" />
            Available for work
          </a>
        </div>
      </div>
    </motion.header>
  );
};

export default Navigation;
